import React, { useCallback, useEffect, useId, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ClipboardList, GraduationCap, Info, Phone, UserRoundCheck } from 'lucide-react';
import PageHead from '../../components/PageHead/PageHead';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import Dropdown from '../../components/Dropdown/Dropdown';
import DatePicker from '../../components/DatePicker/DatePicker';
import TimePicker from '../../components/TimePicker/TimePicker';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import LoadingState from '../../components/LoadingState/LoadingState';
import usePageTitle from '../../hooks/usePageTitle';
import { convertLead, getLead, getLeadAssignees, updateLead } from '../../api/leads';
import { useI18n } from '../../i18n/I18nContext';
import { errorText } from '../../utils/errorText';
import { formatDateTime, formatPhone, formatRelative, todayYmd } from '../../utils/datetime';
import CallLogForm from './CallLogForm';
import LeadTimeline from './LeadTimeline';
import {
  EDITABLE_STATUSES, STATUS_TONE, callState, formatCallTime, fromInstant, leadName, sourceLabel, toInstant,
} from './leadUi';
import './Leads.css';

const SURVEY_FIELDS = ['learningPurpose', 'courseExpectations', 'previousExperience', 'difficulties'];

/**
 * One lead (/leads/:id): who they are and what they asked, the status and owner (editable), the
 * next call, the call log form and the history. "O'quvchiga aylantirish" opens (or links) the
 * student account with the lead's phone; after that the student page is one click away.
 */
function LeadDetail() {
  const { leadId } = useParams();
  const { t } = useI18n();
  const uid = useId();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState(null); // { notFound } | { text }
  const [staff, setStaff] = useState([]);
  const [askConvert, setAskConvert] = useState(false);
  const [converting, setConverting] = useState(false);
  const [editingCall, setEditingCall] = useState(false);
  const [callDraft, setCallDraft] = useState({ date: '', time: '10:00' });

  const lead = detail?.lead || null;
  const name = lead ? leadName(lead) : '';
  usePageTitle(name || t('leads.title'));

  const load = useCallback(async () => {
    setLoading(true);
    setFailure(null);
    try {
      setDetail(await getLead(leadId));
    } catch (err) {
      setFailure(err?.code === 404 ? { notFound: true } : { text: errorText(err, t('leads.detail.loadError')) });
    } finally {
      setLoading(false);
    }
  }, [leadId, t]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    let cancelled = false;
    getLeadAssignees().then((list) => { if (!cancelled) setStaff(list); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const save = async (body) => {
    try {
      setDetail(await updateLead(leadId, body));
      toast.success(t('leads.detail.saved'));
      return true;
    } catch (err) {
      toast.error(errorText(err, t('leads.detail.saveError')));
      return false;
    }
  };

  const convert = async () => {
    setConverting(true);
    try {
      const data = await convertLead(leadId);
      setDetail({ lead: data.lead, activities: data.activities });
      setAskConvert(false);
      toast.success(t(data.accountCreated ? 'leads.detail.convertDone' : 'leads.detail.convertLinked'));
    } catch (err) {
      toast.error(errorText(err, t('leads.detail.convertError')));
    } finally {
      setConverting(false);
    }
  };

  if (loading && !detail) {
    return <div className="lead-page"><LoadingState skeleton="page" message={t('common.loading')} /></div>;
  }
  if (failure) {
    return (
      <div className="lead-page">
        <PageHead back={{ to: '/leads' }} eyebrow={t('leads.title')} title={t('leads.title')} />
        {failure.notFound ? (
          <PageEmptyState variant="search" title={t('leads.detail.notFoundTitle')} description={t('leads.detail.notFoundDesc')} />
        ) : (
          <PageEmptyState
            variant="error"
            title={t('leads.detail.loadError')}
            description={failure.text !== t('leads.detail.loadError') ? failure.text : undefined}
            onRetry={load}
          />
        )}
      </div>
    );
  }
  if (!lead) return null;

  const converted = lead.status === 'CONVERTED';
  const state = callState(lead.nextCallAt);
  const phone = formatPhone(lead.phoneNumber) || lead.phoneNumber;
  const survey = SURVEY_FIELDS.filter((key) => lead[key]);

  const ownerOptions = [
    { value: '', label: t('leads.noOwner') },
    ...staff.map((s) => ({ value: s.id, label: s.name })),
  ];
  // The current owner stays choosable even when the list could not be read.
  if (lead.assignedTo && !staff.some((s) => s.id === lead.assignedTo.id)) {
    ownerOptions.push({ value: lead.assignedTo.id, label: lead.assignedTo.name || '—' });
  }

  const startCallEdit = () => {
    const current = fromInstant(lead.nextCallAt);
    setCallDraft({ date: current.date, time: current.time || '10:00' });
    setEditingCall(true);
  };
  const saveCall = async () => {
    if (await save({ nextCallAt: toInstant(callDraft.date, callDraft.time) })) setEditingCall(false);
  };

  const actions = (
    <>
      <a className="ds-btn ds-btn-secondary" href={`tel:${lead.phoneNumber}`}>
        <Phone aria-hidden="true" />
        {t('leads.detail.call')}
      </a>
      {converted && lead.userId ? (
        <Link className="ds-btn ds-btn-primary" to={`/students/${lead.userId}`}>
          <GraduationCap aria-hidden="true" />
          {t('leads.detail.openStudent')}
        </Link>
      ) : (
        <button type="button" className="ds-btn ds-btn-primary" onClick={() => setAskConvert(true)}>
          <UserRoundCheck aria-hidden="true" />
          {t('leads.detail.convert')}
        </button>
      )}
    </>
  );

  return (
    <div className="lead-page">
      <PageHead
        back={{ to: '/leads' }}
        eyebrow={t('leads.title')}
        title={name}
        description={phone}
        actions={actions}
      />

      <div className="page-grid page-grid--rail lead-grid">
        <div className="ui-stack">
          {!converted && <CallLogForm leadId={lead.id} status={lead.status} onSaved={setDetail} />}
          <LeadTimeline leadId={lead.id} activities={detail.activities || []} people={staff} onChanged={setDetail} />
        </div>

        <aside className="ui-stack lead-rail">
          <section className="section-card lead-facts" aria-labelledby={`${uid}-facts`}>
            <div className="lead-card-head">
              <span className="ui-icon-chip ui-icon-chip--md" data-tone="brand" aria-hidden="true"><Info /></span>
              <h2 className="lead-card-title" id={`${uid}-facts`}>{name}</h2>
            </div>

            <div className="ui-field">
              <span className="ui-label" id={`${uid}-status`}>{t('leads.detail.status')}</span>
              {converted ? (
                <StatusBadge tone={STATUS_TONE.CONVERTED}>{t('leads.status.CONVERTED')}</StatusBadge>
              ) : (
                <Dropdown
                  value={lead.status}
                  onChange={(v) => { if (v !== lead.status) save({ status: v }); }}
                  options={EDITABLE_STATUSES.map((s) => ({ value: s, label: t(`leads.status.${s}`) }))}
                  labelledBy={`${uid}-status`}
                />
              )}
            </div>

            <div className="ui-field">
              <span className="ui-label" id={`${uid}-owner`}>{t('leads.detail.owner')}</span>
              <Dropdown
                value={lead.assignedTo?.id || ''}
                onChange={(v) => { if (v !== (lead.assignedTo?.id || '')) save({ assignedToId: v || null }); }}
                options={ownerOptions}
                labelledBy={`${uid}-owner`}
              />
            </div>

            {!converted && (
              <div className="ui-field">
                <span className="ui-label">{t('leads.detail.nextCall')}</span>
                {editingCall ? (
                  <>
                    <div className="ui-picker-pair">
                      <DatePicker
                        value={callDraft.date}
                        onChange={(v) => setCallDraft((d) => ({ ...d, date: v }))}
                        min={todayYmd()}
                        aria-label={`${t('leads.detail.nextCall')}: ${t('leads.form.date')}`}
                      />
                      <TimePicker
                        value={callDraft.time}
                        onChange={(v) => setCallDraft((d) => ({ ...d, time: v || '10:00' }))}
                        aria-label={`${t('leads.detail.nextCall')}: ${t('leads.form.time')}`}
                      />
                    </div>
                    <div className="lead-inline-actions">
                      <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={() => setEditingCall(false)}>{t('leads.form.cancel')}</button>
                      <button type="button" className="ds-btn ds-btn-primary ds-btn-sm" onClick={saveCall} disabled={!callDraft.date}>{t('common.save')}</button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className={`lead-next lead-next--${state || 'none'}`}>
                      {state ? formatCallTime(t, lead.nextCallAt) : t('leads.notPlanned')}
                      {state === 'overdue' && <span className="leads-call__flag">{t('leads.overdue')}</span>}
                    </p>
                    <div className="lead-inline-actions">
                      <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={startCallEdit}>
                        {t(lead.nextCallAt ? 'leads.detail.reschedule' : 'leads.detail.planCall')}
                      </button>
                      {lead.nextCallAt && (
                        <button type="button" className="ds-btn ds-btn-ghost ds-btn-sm" onClick={() => save({ nextCallAt: null })}>
                          {t('leads.detail.cancelCall')}
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            <dl className="ui-kv">
              <div className="ui-kv__row">
                <dt>{t('leads.detail.lastContact')}</dt>
                <dd>{lead.lastContactedAt ? formatRelative(lead.lastContactedAt) : t('leads.never')}</dd>
              </div>
              {lead.course && (
                <div className="ui-kv__row">
                  <dt>{t('leads.detail.course')}</dt>
                  <dd>{lead.course.title || '—'}</dd>
                </div>
              )}
              <div className="ui-kv__row">
                <dt>{t('leads.detail.source')}</dt>
                <dd>{sourceLabel(t, lead.source)}</dd>
              </div>
              {lead.contactTime && (
                <div className="ui-kv__row">
                  <dt>{t('leads.detail.contactTime')}</dt>
                  <dd>{lead.contactTime}</dd>
                </div>
              )}
              <div className="ui-kv__row">
                <dt>{t('leads.detail.created')}</dt>
                <dd>{formatDateTime(lead.createdAt, { year: 'auto' })}</dd>
              </div>
            </dl>
            {lead.notes && (
              <div className="lead-notes">
                <span className="ui-label">{t('leads.detail.notes')}</span>
                <p>{lead.notes}</p>
              </div>
            )}
          </section>

          {survey.length > 0 && (
            <section className="section-card lead-survey" aria-labelledby={`${uid}-survey`}>
              <div className="lead-card-head">
                <span className="ui-icon-chip ui-icon-chip--md" data-tone="purple" aria-hidden="true"><ClipboardList /></span>
                <h2 className="lead-card-title" id={`${uid}-survey`}>{t('leads.detail.survey')}</h2>
              </div>
              <dl className="lead-survey__list">
                {survey.map((key) => (
                  <div key={key}>
                    <dt>{t(`leads.detail.${key}`)}</dt>
                    <dd>{lead[key]}</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}
        </aside>
      </div>

      <ConfirmDialog
        open={askConvert}
        title={t('leads.detail.convertTitle', { name })}
        description={t('leads.detail.convertDesc')}
        confirmLabel={t('leads.detail.convertConfirm')}
        tone="primary"
        loading={converting}
        onConfirm={convert}
        onCancel={() => setAskConvert(false)}
      />
    </div>
  );
}

export default LeadDetail;
